/**
 * Lấy mẫu ISO 2859-1 (kiểm tra thường, lấy mẫu một lần) và quy tắc kết luận phiếu QC.
 *
 * Nguồn duy nhất cho cỡ mẫu, Ac/Re và kết luận Đạt / Không đạt của IQC, FQC (web dùng qua
 * apps/web/src/lib/aql.ts, máy chủ dùng trực tiếp trong qc-materials / qc-final service).
 *
 * Quy tắc:
 * - Cỡ mẫu theo Bảng 1 (mã chữ theo cỡ lô + mức kiểm), Ac/Re theo Bảng 2-A. Ô có mũi tên được
 *   đi theo mũi tên tới phương án gần nhất (cỡ mẫu đổi theo phương án đó).
 * - Lỗi nghiêm trọng luôn Ac = 0 (có 1 lỗi là Không đạt).
 * - AQL nặng và AQL nhẹ tra riêng. Nếu hai phương án cho cỡ mẫu khác nhau, lấy cỡ mẫu lớn hơn
 *   (chặt hơn) và giữ Ac/Re của từng phương án.
 * - Cỡ mẫu ≥ cỡ lô thì kiểm 100% lô, Ac/Re giữ theo bảng.
 * - Cách kiểm 100%: cỡ mẫu = cỡ lô; lô chỉ Đạt khi không có lỗi của nhóm được chọn:
 *   luôn xét lỗi nghiêm trọng + nặng (Ac 0 / Re 1); lỗi nhẹ xét (Ac 0 / Re 1) khi chọn "Mọi lỗi",
 *   bỏ qua khi chọn "Nặng trở lên" (Ac/Re nhẹ để trống).
 */

export type QcInspectionMode = 'AQL' | 'FULL';
export type QcInspectionLevel = 'I' | 'II' | 'III' | 'S-1' | 'S-2' | 'S-3' | 'S-4';

/** Mã chữ cỡ mẫu theo thứ tự trong bảng (không có I, O) */
export const AQL_CODE_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R'] as const;
/** Cỡ mẫu tương ứng từng mã chữ */
export const AQL_SAMPLE_SIZES = [2, 3, 5, 8, 13, 20, 32, 50, 80, 125, 200, 315, 500, 800, 1250, 2000] as const;
/** Cột AQL của Bảng 2-A (0.010 … 10) */
const AQL_COLUMNS = [0.01, 0.015, 0.025, 0.04, 0.065, 0.1, 0.15, 0.25, 0.4, 0.65, 1.0, 1.5, 2.5, 4.0, 6.5, 10] as const;
/** Giá trị AQL cho người dùng chọn */
export const AQL_SELECTABLE_VALUES = [0.065, 0.1, 0.15, 0.25, 0.4, 0.65, 1.0, 1.5, 2.5, 4.0, 6.5] as const;

// Bảng 1: cận trên của khoảng cỡ lô -> mã chữ cho từng mức kiểm
const LOT_RANGES: Array<{ max: number; codes: Record<QcInspectionLevel, string> }> = [
  { max: 8, codes: { 'S-1': 'A', 'S-2': 'A', 'S-3': 'A', 'S-4': 'A', I: 'A', II: 'A', III: 'B' } },
  { max: 15, codes: { 'S-1': 'A', 'S-2': 'A', 'S-3': 'A', 'S-4': 'A', I: 'A', II: 'B', III: 'C' } },
  { max: 25, codes: { 'S-1': 'A', 'S-2': 'A', 'S-3': 'B', 'S-4': 'B', I: 'B', II: 'C', III: 'D' } },
  { max: 50, codes: { 'S-1': 'A', 'S-2': 'B', 'S-3': 'B', 'S-4': 'C', I: 'C', II: 'D', III: 'E' } },
  { max: 90, codes: { 'S-1': 'B', 'S-2': 'B', 'S-3': 'C', 'S-4': 'C', I: 'C', II: 'E', III: 'F' } },
  { max: 150, codes: { 'S-1': 'B', 'S-2': 'B', 'S-3': 'C', 'S-4': 'D', I: 'D', II: 'F', III: 'G' } },
  { max: 280, codes: { 'S-1': 'B', 'S-2': 'C', 'S-3': 'D', 'S-4': 'E', I: 'E', II: 'G', III: 'H' } },
  { max: 500, codes: { 'S-1': 'B', 'S-2': 'C', 'S-3': 'D', 'S-4': 'E', I: 'F', II: 'H', III: 'J' } },
  { max: 1200, codes: { 'S-1': 'C', 'S-2': 'C', 'S-3': 'E', 'S-4': 'F', I: 'G', II: 'J', III: 'K' } },
  { max: 3200, codes: { 'S-1': 'C', 'S-2': 'D', 'S-3': 'E', 'S-4': 'G', I: 'H', II: 'K', III: 'L' } },
  { max: 10000, codes: { 'S-1': 'C', 'S-2': 'D', 'S-3': 'F', 'S-4': 'G', I: 'J', II: 'L', III: 'M' } },
  { max: 35000, codes: { 'S-1': 'C', 'S-2': 'D', 'S-3': 'F', 'S-4': 'H', I: 'K', II: 'M', III: 'N' } },
  { max: 150000, codes: { 'S-1': 'D', 'S-2': 'E', 'S-3': 'G', 'S-4': 'J', I: 'L', II: 'N', III: 'P' } },
  { max: 500000, codes: { 'S-1': 'D', 'S-2': 'E', 'S-3': 'G', 'S-4': 'J', I: 'M', II: 'P', III: 'Q' } },
  { max: Number.POSITIVE_INFINITY, codes: { 'S-1': 'D', 'S-2': 'E', 'S-3': 'H', 'S-4': 'K', I: 'N', II: 'Q', III: 'R' } },
];

/**
 * Bảng 2-A có dạng đường chéo: với d = chỉ số mã chữ + chỉ số cột AQL
 *   d < 14: mũi tên xuống · d = 14: 0/1 · d = 15: mũi tên lên · d = 16: mũi tên xuống
 *   d = 17…24: 1/2, 2/3, 3/4, 5/6, 7/8, 10/11, 14/15, 21/22 · d ≥ 25: mũi tên lên
 */
const AC_BY_DIAGONAL: Record<number, number> = { 14: 0, 17: 1, 18: 2, 19: 3, 20: 5, 21: 7, 22: 10, 23: 14, 24: 21 };

export interface AqlSinglePlan {
  /** Mã chữ theo cỡ lô + mức kiểm */
  codeLetter: string;
  /** Mã chữ của phương án thực dùng (sau khi đi theo mũi tên) */
  planLetter: string;
  /** Cỡ mẫu của phương án (chưa giới hạn theo cỡ lô) */
  planSampleSize: number;
  ac: number;
  re: number;
}

export interface QcSamplingPlan {
  mode: QcInspectionMode;
  lotSize: number;
  level: QcInspectionLevel;
  codeLetter: string;
  /** Cỡ mẫu phải kiểm (đã giới hạn theo cỡ lô) */
  sampleSize: number;
  /** true khi kiểm toàn bộ lô (cách kiểm 100% hoặc cỡ mẫu ≥ cỡ lô) */
  fullInspection: boolean;
  aqlMajor: number | null;
  aqlMinor: number | null;
  acMajor: number;
  reMajor: number;
  /** null = không xét lỗi nhẹ (cách kiểm 100%, nhóm "Nặng trở lên") */
  acMinor: number | null;
  reMinor: number | null;
}

export function normalizeInspectionLevel(level?: string | null): QcInspectionLevel {
  const v = String(level || 'II').toUpperCase().replace(/^LEVEL\s*/, '').trim();
  if (v === 'I' || v === 'II' || v === 'III' || v === 'S-1' || v === 'S-2' || v === 'S-3' || v === 'S-4') return v;
  return 'II';
}

export function normalizeInspectionMode(mode?: string | null): QcInspectionMode {
  return String(mode || '').toUpperCase() === 'FULL' ? 'FULL' : 'AQL';
}

/** Mã chữ cỡ mẫu (Bảng 1) */
export function getAqlCodeLetter(lotSize: number, level?: string | null): string {
  const lot = Math.max(0, Math.floor(Number(lotSize) || 0));
  const lv = normalizeInspectionLevel(level);
  const row = LOT_RANGES.find((r) => lot <= r.max) || LOT_RANGES[LOT_RANGES.length - 1];
  return row.codes[lv];
}

function aqlColumnIndex(aql: number): number {
  const v = Number(aql);
  let best = 0;
  let bestDiff = Number.POSITIVE_INFINITY;
  AQL_COLUMNS.forEach((col, idx) => {
    const diff = Math.abs(col - v);
    if (diff < bestDiff) {
      best = idx;
      bestDiff = diff;
    }
  });
  return best;
}

/** Tra Bảng 2-A cho một mã chữ + một AQL, đi theo mũi tên nếu có */
export function lookupAqlPlan(codeLetter: string, aql: number): AqlSinglePlan {
  const startIdx = Math.max(0, AQL_CODE_LETTERS.indexOf(codeLetter as (typeof AQL_CODE_LETTERS)[number]));
  const col = aqlColumnIndex(aql);
  let idx = startIdx;
  for (let guard = 0; guard < 40; guard++) {
    const d = idx + col;
    if (d in AC_BY_DIAGONAL) {
      const ac = AC_BY_DIAGONAL[d];
      return {
        codeLetter: AQL_CODE_LETTERS[startIdx],
        planLetter: AQL_CODE_LETTERS[idx],
        planSampleSize: AQL_SAMPLE_SIZES[idx],
        ac,
        re: ac + 1,
      };
    }
    // d < 14 hoặc d = 16: mũi tên xuống (phương án đầu tiên phía dưới); d = 15 hoặc d ≥ 25: mũi tên lên
    const goDown = d < 14 || d === 16;
    const next = goDown ? idx + 1 : idx - 1;
    if (next < 0 || next >= AQL_CODE_LETTERS.length) break;
    idx = next;
  }
  // Ngoài bảng (không xảy ra với các AQL cho chọn): kiểm theo mã chữ gốc, không chấp nhận lỗi
  return {
    codeLetter: AQL_CODE_LETTERS[startIdx],
    planLetter: AQL_CODE_LETTERS[startIdx],
    planSampleSize: AQL_SAMPLE_SIZES[startIdx],
    ac: 0,
    re: 1,
  };
}

export interface QcSamplingInput {
  lotSize: number;
  mode?: string | null;
  level?: string | null;
  aqlMajor?: number | null;
  aqlMinor?: number | null;
  /** Cách kiểm 100%: true = xét cả lỗi nhẹ (Mọi lỗi), false = chỉ nặng trở lên */
  fullCountMinor?: boolean;
}

/** Cỡ mẫu + Ac/Re nặng + Ac/Re nhẹ cho một lô */
export function planQcSampling(input: QcSamplingInput): QcSamplingPlan {
  const lotSize = Math.max(0, Math.floor(Number(input.lotSize) || 0));
  const mode = normalizeInspectionMode(input.mode);
  const level = normalizeInspectionLevel(input.level);
  const codeLetter = lotSize >= 2 ? getAqlCodeLetter(lotSize, level) : 'A';
  const aqlMajor = input.aqlMajor !== undefined && input.aqlMajor !== null && Number(input.aqlMajor) > 0 ? Number(input.aqlMajor) : null;
  const aqlMinor = input.aqlMinor !== undefined && input.aqlMinor !== null && Number(input.aqlMinor) > 0 ? Number(input.aqlMinor) : null;

  if (mode === 'FULL') {
    const countMinor = input.fullCountMinor !== false;
    return {
      mode,
      lotSize,
      level,
      codeLetter,
      sampleSize: lotSize,
      fullInspection: true,
      aqlMajor,
      aqlMinor,
      acMajor: 0,
      reMajor: 1,
      acMinor: countMinor ? 0 : null,
      reMinor: countMinor ? 1 : null,
    };
  }

  const major = lookupAqlPlan(codeLetter, aqlMajor ?? 1.5);
  const minor = aqlMinor !== null ? lookupAqlPlan(codeLetter, aqlMinor) : null;
  const planSample = Math.max(major.planSampleSize, minor ? minor.planSampleSize : 0);
  const sampleSize = lotSize > 0 ? Math.min(planSample, lotSize) : 0;
  return {
    mode,
    lotSize,
    level,
    codeLetter,
    sampleSize,
    fullInspection: lotSize > 0 && planSample >= lotSize,
    aqlMajor: aqlMajor ?? 1.5,
    aqlMinor,
    acMajor: major.ac,
    reMajor: major.re,
    acMinor: minor ? minor.ac : null,
    reMinor: minor ? minor.re : null,
  };
}

// ───────────────────────── Đánh giá chỉ tiêu ─────────────────────────

export type QcCriterionResult = 'PASS' | 'FAIL' | 'NA' | 'PENDING';

function toNumber(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Chỉ tiêu định lượng có cận min/max để tự đánh giá */
export function isMeasurableCriterion(item: { type?: string | null; minValue?: unknown; maxValue?: unknown }): boolean {
  const quantitative = item?.type === 'QUANTITATIVE' || item?.type === 'EVALUATION';
  return quantitative && (toNumber(item.minValue) !== null || toNumber(item.maxValue) !== null);
}

/** Đánh giá số đo theo min–max (gồm cả hai cận). null khi chưa có số đo hoặc không có cận */
export function evaluateMeasuredValue(
  measured: unknown,
  minValue?: unknown,
  maxValue?: unknown,
): 'PASS' | 'FAIL' | null {
  const v = toNumber(measured);
  const min = toNumber(minValue);
  const max = toNumber(maxValue);
  if (v === null || (min === null && max === null)) return null;
  if (min !== null && v < min) return 'FAIL';
  if (max !== null && v > max) return 'FAIL';
  return 'PASS';
}

// ───────────────────────── Kết luận ─────────────────────────

export interface QcDefectCounts {
  critical: number;
  major: number;
  minor: number;
}

export interface QcVerdictInput {
  counts: QcDefectCounts;
  reMajor?: number | null;
  reMinor?: number | null;
  criteria?: Array<{ result?: string | null; name?: string | null }> | null;
}

export interface QcVerdict {
  verdict: 'PASSED' | 'FAILED';
  reasons: string[];
  failedCriteria: number;
  pendingCriteria: number;
}

/**
 * Kết luận tự động:
 * Không đạt khi lỗi nghiêm trọng > 0, hoặc lỗi nặng ≥ Re nặng, hoặc lỗi nhẹ ≥ Re nhẹ,
 * hoặc có chỉ tiêu Không đạt. Ngược lại Đạt. Re để trống thì không xét nhóm lỗi đó.
 */
export function computeQcVerdict(input: QcVerdictInput): QcVerdict {
  const critical = Math.max(0, Number(input.counts?.critical) || 0);
  const major = Math.max(0, Number(input.counts?.major) || 0);
  const minor = Math.max(0, Number(input.counts?.minor) || 0);
  const reMajor = toNumber(input.reMajor);
  const reMinor = toNumber(input.reMinor);
  const criteria = Array.isArray(input.criteria) ? input.criteria : [];
  const failedCriteria = criteria.filter((c) => String(c?.result || '').toUpperCase() === 'FAIL').length;
  const pendingCriteria = criteria.filter((c) => {
    const r = String(c?.result || '').toUpperCase();
    return r === '' || r === 'PENDING';
  }).length;

  const reasons: string[] = [];
  if (critical > 0) reasons.push(`Có ${critical} lỗi nghiêm trọng`);
  if (reMajor !== null && reMajor > 0 && major >= reMajor) reasons.push(`Lỗi nặng ${major} ≥ Re ${reMajor}`);
  if (reMinor !== null && reMinor > 0 && minor >= reMinor) reasons.push(`Lỗi nhẹ ${minor} ≥ Re ${reMinor}`);
  if (failedCriteria > 0) reasons.push(`${failedCriteria} chỉ tiêu không đạt`);

  return { verdict: reasons.length > 0 ? 'FAILED' : 'PASSED', reasons, failedCriteria, pendingCriteria };
}

/** Đếm lỗi theo mức từ bảng "Lỗi phát hiện" */
export function summarizeQcDefects(items: unknown): QcDefectCounts & { total: number } {
  const list = Array.isArray(items) ? items : [];
  let critical = 0;
  let major = 0;
  let minor = 0;
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as Record<string, unknown>;
    const qty = Math.max(0, Math.round(Number(it.defectQty ?? it.qty ?? it.count ?? 0) || 0));
    const sev = String(it.severity || 'MAJOR').toUpperCase();
    if (sev === 'CRITICAL') critical += qty;
    else if (sev === 'MINOR') minor += qty;
    else major += qty;
  }
  return { critical, major, minor, total: critical + major + minor };
}

/**
 * Trạng thái được lưu: kết luận lưu không được nhẹ hơn số liệu.
 * - Trạng thái đang kiểm (PENDING, INSPECTING, REWORK, RE_INSPECT) giữ nguyên.
 * - WARNING (Nhân nhượng) phải có lý do.
 * - PASSED khi số liệu cho Không đạt -> FAILED. FAILED do người kiểm chọn giữ nguyên (chặt hơn).
 */
export function resolveQcStoredStatus(
  requested: string | null | undefined,
  computed: 'PASSED' | 'FAILED',
  overrideReason?: string | null,
): { status: string; error?: string } {
  const req = String(requested || '').toUpperCase();
  if (req === 'PENDING' || req === 'INSPECTING' || req === 'REWORK' || req === 'RE_INSPECT') return { status: req };
  if (req === 'WARNING' || req === 'CONDITIONAL') {
    if (!String(overrideReason || '').trim()) {
      return { status: 'WARNING', error: 'Nhân nhượng phải ghi lý do' };
    }
    return { status: 'WARNING' };
  }
  if (req === 'FAILED' || req === 'FAIL') return { status: 'FAILED' };
  return { status: computed };
}
